/**
 * Hook: `usePersistOnboardingData`
 *
 * Persists the in-progress onboarding draft to the backend when the user
 * reaches `/store`. This is the cut-1 + cut-2 frontend slice of the
 * `onboarding-persistence-backend` change.
 *
 * Behavior:
 *   1. Reads `userData` and `localStorage["carriapp:onboarding"]` on mount.
 *   2. Skips when any prerequisite is missing:
 *        - no draft in localStorage
 *        - draft incomplete (`monthly_spend` or `purchase_frequency` null)
 *        - no authenticated `userData.id`
 *   3. Posts the cut-1 payload through `saveOnboardingData` (which goes
 *      through `apiClient.post` to `endPointUserOnboarding`).
 *   4. On canonical success (`200 { saved: true, onb_completed: true }`):
 *      removes the persisted draft via `clearOnboardingState`. The
 *      service is responsible for validating the response shape — see
 *      `saveOnboardingData` and `isCanonicalOnboardingResponse`. Any
 *      non-canonical 2xx (null body, missing flag, etc.) makes the service
 *      reject, which routes through step 5 instead.
 *   5. On any failure — network error, non-2xx HTTP status, OR non-canonical
 *      response from the service: leaves the draft in localStorage so the
 *      next `/store` visit can retry.
 *   6. Dedupes concurrent / remounted effect runs with a `useRef` guard
 *      so React StrictMode double-mounts (or a same-tab remount while the
 *      POST is in flight) never start a second concurrent request.
 *
 * Important: the hook does NOT depend on `userData.onb_completed`. The
 * backend now treats `onb_completed` as server-authoritative and only
 * flips it to `true` after a successful `POST /api/users/onboarding`.
 * Requiring `onb_completed === true` BEFORE the POST would deadlock the
 * persistence flow (no draft → no POST → onb_completed stays false → still
 * no POST). The frontend fires the POST whenever the auth/draft guards
 * are satisfied; the backend is the sole source of truth for the flag.
 *
 * Mounted from `MainPage` at `/store`. The hook does not return anything
 * — it is a fire-and-forget side effect.
 */

import { useEffect, useRef } from "react";
import apiClient from "modules/main/services/apiClient";
import { saveOnboardingData } from "modules/onboarding/services/save-onboarding-data";
import {
  ONBOARDING_STORAGE_KEY,
  clearOnboardingState,
  readOnboardingState,
  type OnboardingState,
} from "modules/onboarding/types/onboarding-state";
import type { UserData } from "modules/authentication/types/user-data";

/** localStorage key the AuthProvider uses for the authenticated profile. */
const USER_DATA_STORAGE_KEY = "userData";

/**
 * Reads and minimally validates the `userData` JSON the AuthProvider writes.
 * Returns `null` for any failure so callers can short-circuit cleanly.
 *
 * Only the fields the hook actually uses are validated; the AuthProvider
 * already owns shape correctness for the rest of the contract.
 */
const readUserData = (): UserData | null => {
  if (typeof window === "undefined" || !window.localStorage) {
    return null;
  }
  const raw = window.localStorage.getItem(USER_DATA_STORAGE_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<UserData>;
    if (!parsed || typeof parsed !== "object") {
      return null;
    }
    if (typeof parsed.id !== "number") {
      return null;
    }
    return parsed as UserData;
  } catch {
    return null;
  }
};

/**
 * Decides whether the persisted draft is complete enough to attempt a save.
 * The builder in the service also enforces this, but checking here avoids
 * the throw path and keeps the hook's skip behavior testable.
 */
const isDraftComplete = (draft: OnboardingState | null): draft is OnboardingState =>
  draft !== null &&
  typeof draft.monthly_spend === "number" &&
  draft.monthly_spend > 0 &&
  draft.purchase_frequency !== null;

interface PersistGuard {
  /** True once the hook has already issued (or is issuing) a POST. */
  fired: boolean;
}

/**
 * Mounts the post-auth onboarding persistence side-effect.
 *
 * The hook is intentionally a "void return" — its only output is the
 * network call and the localStorage cleanup. Components should call it
 * once at the top of their render body (MainPage does this).
 */
export const usePersistOnboardingData = (onPersisted?: () => void): void => {
  // useRef survives StrictMode double-mount without re-rendering. The
  // boolean is flipped to `true` synchronously inside the effect so the
  // second invocation short-circuits before any IO happens.
  const guardRef = useRef<PersistGuard>({ fired: false });

  useEffect(() => {
    if (guardRef.current.fired) {
      return;
    }
    guardRef.current.fired = true;

    const draft = readOnboardingState();
    const userData = readUserData();

    if (!isDraftComplete(draft)) {
      // No-op: a user may reach /store with no draft or an incomplete
      // draft (e.g. abandoned the wizard early). No backend call is made
      // and no draft is touched.
      return;
    }

    if (!userData) {
      // Draft is complete but no authenticated profile is available.
      // Preserve the draft so the next /store visit (after login) can
      // pick it up and flush it.
      return;
    }

    // Fire-and-forget: the hook does not expose state. The async work is
    // intentionally not awaited so the render path is not blocked; the
    // draft cleanup / retention decision lives inside the `.then` /
    // `.catch` chain so it is race-safe against StrictMode.
    //
    // NOTE: we deliberately do NOT require `userData.onb_completed === true`
    // here. The backend sets that flag server-side after a successful
    // POST; gating on it client-side would prevent the POST from ever
    // firing for a freshly-completed onboarding.
    saveOnboardingData(userData.id, draft)
      .then(() => {
        clearOnboardingState();
        onPersisted?.();
      })
      .catch((error: unknown) => {
        // Preserve the draft so the next /store visit can retry.
        // Reporting is owned by apiClient — we only log to surface
        // unexpected error shapes in dev.
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn(
            "[usePersistOnboardingData] POST failed, retaining draft:",
            error,
          );
        }
        // Touch `apiClient` so the bundler keeps the import alive for
        // type-aware tooling (the import is also used transitively via
        // `saveOnboardingData`).
        void apiClient;
        void ONBOARDING_STORAGE_KEY;
      });
  }, []);
};